import { useCallback, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { useNotification } from '@/_shared/hooks';
import { getDelegatesCall, postCreateDelegateGuestCall } from '@/organization/state';

const EMAIL_PATTERN = /\S+@\S+\.\S+/;

type Props = {
  closeDialog: () => void;
};

export const DelegateGuest = ({ closeDialog }: Props) => {
  const t = useTranslations('settings.tabs.delegates.addDelegate.dialog');
  const tShared = useTranslations();
  const { notify } = useNotification();

  const { execute: executePostCreateDelegateGuest, loading: loadingPostCreateDelegateGuest } =
    postCreateDelegateGuestCall.hook();

  const { execute: executeGetDelegates } = getDelegatesCall.hook();

  const {
    handleSubmit,
    formState: { isValid, errors: formErrors, touchedFields },
    control,
    watch,
    // onChange keeps isValid reactive so the CTA enables while typing;
    // field error messages are still gated on blur via touchedFields below.
  } = useForm({ mode: 'onChange' });

  const fieldValues = watch();
  const someInputFilled = useMemo(() => Object.values(fieldValues).some(Boolean), [fieldValues]);

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    data => {
      if (isValid) {
        const { firstName, lastName, email } = data;
        executePostCreateDelegateGuest({
          firstName,
          lastName,
          email,
          successSaga: () => {
            notify(tShared('success.genericApiSuccess'), 'success');
            executeGetDelegates({
              start: 0,
              length: 1000,
            });
            closeDialog();
          },
          errorSaga: () => {
            notify(tShared('errors.genericApiError'), 'error');
            closeDialog();
          },
        });
      }
    },
    [isValid, executePostCreateDelegateGuest, notify, tShared, closeDialog, executeGetDelegates],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box>
        <Box mb='xs' display='flex' flexDirection='row' flex={1} justifyContent='space-between'>
          <Box flex={1}>
            <Form.FormInput
              control={control}
              name='firstName'
              label={t('fields.firstName')}
              placeholder={t('fields.firstName')}
              additonalProps={{
                required: true,
              }}
            />
          </Box>
          <Box flex={1} ml='xs'>
            <Form.FormInput
              control={control}
              name='lastName'
              label={t('fields.lastName')}
              placeholder={t('fields.lastName')}
              additonalProps={{
                required: true,
              }}
            />
          </Box>
        </Box>
        <Box display='flex' flexDirection='row' flex={1} justifyContent='space-between'>
          <Box flex={1}>
            <Form.FormInput
              control={control}
              name='email'
              label={t('fields.email')}
              placeholder={t('fields.email')}
              additonalProps={{
                required: true,
                pattern: {
                  value: EMAIL_PATTERN,
                  message: t('alertInvalidEmail'),
                },
              }}
              type='email'
              hasError={!!formErrors.email && !!touchedFields.email}
              errorMessage={touchedFields.email ? formErrors.email?.message?.toString() : undefined}
            />
          </Box>
        </Box>
      </Box>
      <Box mt='s' alignItems='center' display='flex' flexDirection='row' justifyContent='center'>
        <Form.Buttons.Cta onClick={closeDialog} mr='s' mt='s'>
          {t('buttons.close')}
        </Form.Buttons.Cta>
        <Form.Buttons.Submit
          loading={loadingPostCreateDelegateGuest}
          disabled={!isValid || !someInputFilled || loadingPostCreateDelegateGuest}
          mt='s'
          data-testid='filter-button-search'
        >
          {t('buttons.save')}
        </Form.Buttons.Submit>
      </Box>
    </form>
  );
};
